/**
 * Pure discount calculation functions.
 * Public API operates in whole kronor (SEK), matching the system convention.
 *
 * Phase 4 split (Sprint D): for new callers, prefer `discount_percent` for
 * percentage types and `discount_value_minor` (öre) for fixed_amount types,
 * falling back to legacy `discount_value` (kr, dual-meaning) when missing.
 *
 * Discount-type names used by this module (canonical, post-mapping):
 *   'percentage' | 'fixed_amount' | 'free' | 'free_sessions'
 * Voucher rows (DB types: 'percent' | 'fixed' | 'free') are mapped to these
 * canonical names by the caller (see useDiscountCodes.validateCode).
 */

export interface DiscountCodeData {
  discount_type: string;
  discount_value: number;
  // Phase 4 split fields — prefer these when present (Sprint D Plan A reads).
  discount_percent?: number | null; // percent value for 'percentage' type
  discount_value_minor?: number | null; // öre value for 'fixed_amount' type
  min_amount_minor?: number | null; // öre threshold for min_amount check
  currency?: string | null;
  max_discount_amount?: number | null;
  min_amount: number | null;
  max_uses: number | null;
  per_user_limit: number | null;
  used_count: number | null;
  valid_from: string | null;
  valid_until: string | null;
  is_active: boolean | null;
  applicable_to: string[] | null;
  specific_membership_type_ids?: string[] | null;
  specific_activity_type_ids?: string[] | null;
  specific_court_ids?: string[] | null;
  weekday_restriction?: number[] | null;
  time_from?: string | null;
  time_to?: string | null;
}

export interface CodeRestrictionContext {
  weekday?: number; // 0=Sun..6=Sat
  time?: string; // "HH:mm"
  membership_type_id?: string;
  activity_type_id?: string;
  court_id?: string;
}

/**
 * Calculate the discount amount to subtract from the total.
 * Returns the discount in kronor (never negative, never exceeds totalAmount).
 */
export function calculateDiscount(
  code: DiscountCodeData,
  totalAmount: number
): number {
  if (totalAmount <= 0) return 0;

  switch (code.discount_type) {
    case "percentage": {
      // Phase 4 split: prefer discount_percent over legacy discount_value.
      const pctRaw = code.discount_percent ?? code.discount_value;
      const pct = Math.min(Math.max(pctRaw, 0), 100);
      let raw = Math.round((totalAmount * pct) / 100);
      // Apply max_discount_amount cap
      if (code.max_discount_amount != null && code.max_discount_amount > 0) {
        raw = Math.min(raw, code.max_discount_amount);
      }
      return Math.min(raw, totalAmount);
    }
    case "fixed_amount": {
      // Phase 4 split: prefer discount_value_minor (öre) over legacy discount_value (kr).
      const valueKr = code.discount_value_minor != null
        ? code.discount_value_minor / 100
        : code.discount_value;
      return Math.min(Math.max(valueKr, 0), totalAmount);
    }
    case "free": {
      return totalAmount;
    }
    case "free_sessions": {
      // Returns original amount as discount (entire session free)
      return totalAmount;
    }
    default:
      return 0;
  }
}

/**
 * Validate code restrictions: weekday, time, and specific IDs.
 * Returns { valid, error? }.
 */
export function validateCodeRestrictions(
  code: DiscountCodeData,
  context: CodeRestrictionContext
): { valid: boolean; error?: string } {
  // Weekday restriction
  if (
    code.weekday_restriction &&
    code.weekday_restriction.length > 0 &&
    context.weekday != null
  ) {
    if (!code.weekday_restriction.includes(context.weekday)) {
      return { valid: false, error: "Koden gäller inte denna veckodag" };
    }
  }

  // Time restriction
  if (code.time_from && code.time_to && context.time) {
    if (context.time < code.time_from || context.time > code.time_to) {
      return {
        valid: false,
        error: `Koden gäller bara mellan ${code.time_from} och ${code.time_to}`,
      };
    }
  }

  // Specific membership type
  if (
    context.membership_type_id &&
    code.specific_membership_type_ids &&
    code.specific_membership_type_ids.length > 0
  ) {
    if (!code.specific_membership_type_ids.includes(context.membership_type_id)) {
      return { valid: false, error: "Koden gäller inte för denna medlemskapstyp" };
    }
  }

  // Specific activity type
  if (
    context.activity_type_id &&
    code.specific_activity_type_ids &&
    code.specific_activity_type_ids.length > 0
  ) {
    if (!code.specific_activity_type_ids.includes(context.activity_type_id)) {
      return { valid: false, error: "Koden gäller inte för denna aktivitetstyp" };
    }
  }

  // Specific court
  if (
    context.court_id &&
    code.specific_court_ids &&
    code.specific_court_ids.length > 0
  ) {
    if (!code.specific_court_ids.includes(context.court_id)) {
      return { valid: false, error: "Koden gäller inte för denna bana" };
    }
  }

  return { valid: true };
}

/**
 * Check whether a discount code is currently valid (date range, active, usage limits).
 * Returns { valid, error? }.
 */
export function validateDiscountCodeSync(
  code: DiscountCodeData,
  opts: {
    totalAmount: number;
    applicableTo?: string;
    userUsageCount?: number;
  }
): { valid: boolean; error?: string } {
  if (!code.is_active) {
    return { valid: false, error: "Koden är inte aktiv" };
  }

  const now = new Date();

  if (code.valid_from && new Date(code.valid_from) > now) {
    return { valid: false, error: "Koden är inte giltig ännu" };
  }

  if (code.valid_until && new Date(code.valid_until) < now) {
    return { valid: false, error: "Koden har gått ut" };
  }

  if (code.max_uses != null && (code.used_count ?? 0) >= code.max_uses) {
    return { valid: false, error: "Koden har förbrukats" };
  }

  if (
    code.per_user_limit != null &&
    opts.userUsageCount != null &&
    opts.userUsageCount >= code.per_user_limit
  ) {
    return { valid: false, error: "Du har redan använt denna kod maximalt antal gånger" };
  }

  // Phase 4 split: prefer min_amount_minor (öre) over legacy min_amount (kr).
  const minKr = code.min_amount_minor != null
    ? code.min_amount_minor / 100
    : code.min_amount;
  if (minKr != null && opts.totalAmount < minKr) {
    return {
      valid: false,
      error: `Minsta belopp för denna kod är ${minKr} kr`,
    };
  }

  // Check applicability
  if (
    opts.applicableTo &&
    code.applicable_to &&
    code.applicable_to.length > 0 &&
    !code.applicable_to.includes("all") &&
    !code.applicable_to.includes(opts.applicableTo)
  ) {
    return { valid: false, error: "Koden gäller inte för denna typ av bokning" };
  }

  return { valid: true };
}

/** Format a discount for display, e.g. "20%" or "50 kr" or "Gratis" */
export function formatDiscountValue(code: DiscountCodeData): string {
  switch (code.discount_type) {
    case "percentage": {
      // Phase 4 split: prefer discount_percent.
      const pct = code.discount_percent ?? code.discount_value;
      return `${pct}%`;
    }
    case "fixed_amount": {
      // Phase 4 split: prefer discount_value_minor (öre → kr).
      const kr = code.discount_value_minor != null
        ? code.discount_value_minor / 100
        : code.discount_value;
      return `${kr} kr`;
    }
    case "free":
      return "Gratis";
    case "free_sessions":
      return "Gratis pass";
    default:
      return `${code.discount_value}`;
  }
}
